import React, { useEffect, useState } from "react";
import "../Zone/Zone.css";
import "./Overview.css";
import { fetchComplaintsPage, fetchComplaintById } from "../../Services/locationEntries";

const PAGE_SIZE = 20;

function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return (
    d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) +
    ", " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
}

const STATUS_VARIANT = {
  OPEN: "bad",
  CLOSED: "ok",
  "UNDER REVIEW": "warn",
  "PENDING CLOSURE": "neutral",
};

const SOURCE_VARIANT = {
  public: "ok",
  customerSupport: "info",
  independentEngineer: "neutral",
};

const SOURCE_LABEL = {
  public: "Public",
  customerSupport: "CS",
  independentEngineer: "IE",
};

function ComplaintRow({ c, onComplaintClick }) {
  const status = c.status?.currentStatus || "OPEN";
  const source = c.issuedBy?.type;
  return (
    <div key={c.documentId} className="lrow" onClick={() => onComplaintClick && onComplaintClick(c)}>
      <div className="li">
        <div className="ln">{c.category || "Complaint"}</div>
        <div className="ls" style={{ fontFamily: "var(--font-mono)", letterSpacing: ".02em" }}>{c.documentId}</div>
        <div className="ls">
          {c.location?.PCTNo ? `${c.location.PCTNo} · ` : ""}{fmtDate(c.createdAt)}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px", flexShrink: 0 }}>
        <span className={`snap-pill ${STATUS_VARIANT[status] || "neutral"}`}>{status}</span>
        {source && (
          <span className={`snap-pill ${SOURCE_VARIANT[source] || "neutral"}`}>
            {SOURCE_LABEL[source] || source}
          </span>
        )}
      </div>
    </div>
  );
}

function HomeComplaints({ className, onBack, onComplaintClick, allowedZones, complaintsCount, range }) {
  const [entries, setEntries] = useState(null);
  const [cursors, setCursors] = useState(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  // undefined = idle, null = loading, false = not found, object = found
  const [searchResult, setSearchResult] = useState(undefined);

  const from = range?.from;
  const to = range?.to;

  useEffect(() => {
    setEntries(null);
    setCursors(undefined);
    setHasMore(false);
    fetchComplaintsPage({ allowedZones, from, to, pageSize: PAGE_SIZE })
      .then((page) => {
        setEntries(page.items);
        setCursors(page.cursors);
        setHasMore(page.hasMore);
      })
      .catch(() => {
        setEntries([]);
        setHasMore(false);
      });
  }, [allowedZones, from, to]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    fetchComplaintsPage({ allowedZones, from, to, pageSize: PAGE_SIZE, cursors })
      .then((page) => {
        setEntries((prev) => [...(prev || []), ...page.items]);
        setCursors(page.cursors);
        setHasMore(page.hasMore);
      })
      .catch(() => setHasMore(false))
      .finally(() => setLoadingMore(false));
  };

  useEffect(() => {
    if (!searchTerm) return;
    setSearchResult(null);
    fetchComplaintById(searchTerm)
      .then((c) => setSearchResult(c ?? false))
      .catch(() => setSearchResult(false));
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    if (!val.trim()) {
      setSearchTerm("");
      setSearchResult(undefined);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputValue.trim().length >= 8) {
      setSearchTerm(inputValue.trim());
    }
  };

  const isSearchActive = !!searchTerm;
  const readyToSearch = inputValue.trim().length >= 8;
  const crumbZones = Array.isArray(allowedZones) && allowedZones.length > 0
    ? `Zone${allowedZones.length > 1 ? "s" : ""} ${allowedZones.join(", ")}`
    : "All PCTs";
  const total = complaintsCount?.total ?? 0;

  return (
    <div className={className} data-cy="home-complaints-screen">
      <div className="subhdr">
        <div className="back" onClick={onBack}>
          <svg viewBox="0 0 24 24">
            <polyline points="15,18 9,12 15,6" />
          </svg>
        </div>
        <div className="subhdr-info">
          <div className="subhdr-crumb">Chennai · {crumbZones}</div>
          <div className="subhdr-title">Complaints</div>
        </div>
        {!isSearchActive && entries && (
          <span className="pill neutral">{entries.length} of {total}</span>
        )}
      </div>
      <div className="divider" />
      <div style={{ padding: "8px 20px 4px" }}>
        <input
          type="text"
          placeholder="Enter complaint ID and press Enter…"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          style={{
            width: "100%", boxSizing: "border-box",
            background: "var(--b0, rgba(255,255,255,0.06))",
            border: `1px solid ${readyToSearch ? "var(--t2)" : "var(--b1)"}`,
            borderRadius: "6px", padding: "7px 10px",
            fontSize: "var(--text-base)", color: "var(--t1)", outline: "none",
            fontFamily: "var(--font-mono)",
            transition: "border-color .15s",
          }}
        />
      </div>
      <div className="scroll">
        {/* Search mode */}
        {isSearchActive && searchResult === null && (
          <div style={{ padding: "24px 20px", fontSize: "var(--text-base)", color: "var(--t3)" }}>Searching…</div>
        )}
        {isSearchActive && searchResult === false && (
          <div style={{ padding: "24px 20px", fontSize: "var(--text-base)", color: "var(--t3)" }}>No complaint found.</div>
        )}
        {isSearchActive && searchResult && (
          <div className="lrows">
            <ComplaintRow c={searchResult} onComplaintClick={onComplaintClick} />
          </div>
        )}

        {/* Default list mode */}
        {!isSearchActive && entries === null && (
          <div style={{ padding: "24px 20px", fontSize: "var(--text-base)", color: "var(--t3)" }}>Loading…</div>
        )}
        {!isSearchActive && entries?.length === 0 && (
          <div style={{ padding: "24px 20px", fontSize: "var(--text-base)", color: "var(--t3)" }}>No complaints found.</div>
        )}
        {!isSearchActive && entries?.length > 0 && (
          <div className="lrows">
            {entries.map((c) => (
              <ComplaintRow key={c.documentId} c={c} onComplaintClick={onComplaintClick} />
            ))}
          </div>
        )}
        {!isSearchActive && hasMore && (
          <div
            data-cy="home-complaints-load-more"
            onClick={loadingMore ? undefined : handleLoadMore}
            style={{
              padding: "14px 20px", textAlign: "center", fontSize: "var(--text-base)",
              color: "var(--t2)", cursor: loadingMore ? "default" : "pointer",
            }}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </div>
        )}

        <div style={{ height: "20px" }} />
      </div>
    </div>
  );
}

export default HomeComplaints;
